import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth } from "./authFetch";
import { TOPUP_BLOCK_CREDITS, TOPUP_BLOCK_USD } from "../pages/PricingPage";

/* The balance is fetched once on mount and again whenever something in the app knows credits
   moved: a finished background run or a completed purchase dispatches `creditsChanged()`.
   No background timer. */
export const CREDITS_CHANGED_EVENT = "credits:changed";
export function creditsChanged() {
  try {
    window.dispatchEvent(new Event(CREDITS_CHANGED_EVENT));
  } catch {}
}

export function useCredits() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/api/credits/balance");
      if (!res.ok) return;
      const body = await res.json().catch(() => null);
      if (body && typeof body.balance === "number") setBalance(body.balance);
    } catch {
      /* Leave the last known balance in place. */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener(CREDITS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(CREDITS_CHANGED_EVENT, refresh);
  }, [refresh]);

  return { balance, loading, refresh };
}

export function formatCredits(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/* Rough dollar value of a credit amount, priced at the top-up rate ($10 per 1,000 → $0.01 each). */
export function formatCreditsUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return "";
  const usd = (Number(n) * TOPUP_BLOCK_USD) / TOPUP_BLOCK_CREDITS;
  return `≈ ${usd.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 })}`;
}

/* What each ledger row was for, in product language — never the vendor or model. */
const FEATURE_LABELS = {
  find_candidates: "Candidate search",
  manual_search: "Manual search",
  contacts_search: "Find contacts",
  lookalike: "Similar candidates",
  people_search_preview: "Search preview",
  brief: "Search brief",
  refine_chat: "Filter tuning",
  filter_agent: "Filter assistant",
  hr_contacts: "HR contact search",
  pdf_upload: "Resume upload",
  email_draft: "Email draft",
  job_match: "Job matching",
  job_import: "Job import",
  job_embedding: "Job indexing",
  seniority: "Candidate levelling",
  track_company: "Company tracking",
  industry_firm: "Industry firm added",
  company_enrich: "Company research",
  find_email: "Email lookup",
};

export function describeLedgerRow(row) {
  if (row.type === "grant") return "Credits added";
  if (row.type === "carryover") return "Carried over";
  if (row.type === "renewal") return "Plan renewed";
  if (row.type === "adjustment") return row.source === "expiry" ? "Credits expired" : "Adjustment";
  return FEATURE_LABELS[row.feature] || "Usage";
}

/* A row that carries its related charges underneath reads as the whole activity. */
const GROUP_LABELS = {
  find_candidates: "Candidate search",
  lookalike: "Similar candidates",
  track_company: "Track companies",
  job_import: "Import jobs",
  pdf_upload: "Import candidates",
  contacts_search: "Find contacts",
};
export function describeLedgerGroup(row) {
  const label = GROUP_LABELS[row.feature] || describeLedgerRow(row);
  // One Find contacts search is one charge per job title searched.
  if (row.feature === "contacts_search") return `${label} · ${(row.children?.length || 0) + 1} titles`;
  // One upload of several files reads as one entry with the file count.
  const files = Number(row.batchFiles) || 0;
  return files > 1 ? `${label} · ${files} files` : label;
}

/* What each line inside an opened group was for, next to its amount. */
export function describeGroupPart(parent, row, isOwn) {
  if (parent.feature === "track_company") return isOwn ? "Tracking" : "Research";
  if (parent.feature === "job_import") {
    if (row.feature === "job_embedding") return "Indexing";
    return "Reading the file";
  }
  if (parent.feature === "pdf_upload") return "Reading resumes";
  if (parent.feature === "contacts_search") return "One job title";
  return isOwn ? "Run" : "Setup";
}
